import {
  Orbit3D,
  Satellite,
  TLEDataProvider,
  TLEProviderCalculator,
  TLEStateProvider,
  Visual3DWidget,
} from "@focus/visual3D";
import { useMemo } from "react";
import type {
  CatalogueObject,
  CatalogueOrbitClass,
  CatalogueStatusCategory,
} from "../../types/catalog";
import { getPropertyObjectValue } from "../../utils/catalogObject";
import { FullscreenPanelCard } from "../FullscreenPanelCard";
import { useFullscreenPanel } from "../useFullscreenPanel";
import { Catalogue3DTools } from "./Catalogue3DTools";

const STATUS_CATEGORIES: CatalogueStatusCategory[] = [
  "ACTIVE",
  "INACTIVE",
  "UNKNOWN",
];

const ORBIT_CLASSES: CatalogueOrbitClass[] = [
  "LLEO",
  "LEO",
  "MEO",
  "GEO",
  "GTO",
  "OTH",
  "UNKNOWN",
];

const ORBIT_CLASS_COLORS: Record<CatalogueOrbitClass, string> = {
  GTO: "#9333ea",
  LEO: "#0f766e",
  MEO: "#2563eb",
  GEO: "#c2410c",
  UNKNOWN: "#71717a",
  LLEO: "#0cc27c",
  OTH: "#eb259c",
};

const ORBIT_CLASS_LABELS: Record<CatalogueOrbitClass, string> = {
  GTO: "GTO",
  LLEO: "LLEO",
  LEO: "LEO",
  MEO: "MEO",
  GEO: "GEO",
  OTH: "OTH",
  UNKNOWN: "UNKNOWN",
};

const CATALOG_ORBIT_TRAIL_SECONDS = 3600;
const CATALOG_ORBIT_LEAD_SECONDS = 3600;
const VISUAL_3D_WORKERS_URL = `${import.meta.env.BASE_URL}static/visual3d-workers`;

interface CatalogTle {
  id: string;
  line1: string;
  line2: string;
  name: string;
}

interface ViewerCatalogObject {
  id: string;
  orbitClass: CatalogueOrbitClass;
  status: CatalogueStatusCategory;
  tle: CatalogTle;
}

const hexToVisualColor = (hex: string) => {
  const normalizedHex = hex.replace("#", "");

  return {
    R: parseInt(normalizedHex.slice(0, 2), 16),
    G: parseInt(normalizedHex.slice(2, 4), 16),
    B: parseInt(normalizedHex.slice(4, 6), 16),
  };
};

const normalizeOrbitClass = (value: string): CatalogueOrbitClass =>
  ORBIT_CLASSES.includes(value as CatalogueOrbitClass)
    ? (value as CatalogueOrbitClass)
    : "UNKNOWN";

const normalizeStatusCategory = (value: string): CatalogueStatusCategory =>
  STATUS_CATEGORIES.includes(value as CatalogueStatusCategory)
    ? (value as CatalogueStatusCategory)
    : "UNKNOWN";

export const CatalogOrbitViewer = ({
  className = "",
  objects,
  viewerClassName = "h-[380px]",
}: {
  className?: string;
  objects: CatalogueObject[];
  viewerClassName?: string;
}) => {
  const viewerObjects = useMemo<ViewerCatalogObject[]>(
    () =>
      objects.reduce<ViewerCatalogObject[]>((acc, object) => {
        const line1 =
          typeof object.tle?.line1 === "string" ? object.tle.line1 : "";
        const line2 =
          typeof object.tle?.line2 === "string" ? object.tle.line2 : "";

        if (!line1 || !line2) {
          return acc;
        }

        const name =
          getPropertyObjectValue(object.name) ||
          (typeof object.tle?.objectName === "string"
            ? object.tle.objectName
            : object.id);

        acc.push({
          id: object.id,
          orbitClass: normalizeOrbitClass(getPropertyObjectValue(object.regime)),
          status: normalizeStatusCategory(getPropertyObjectValue(object.status)),
          tle: {
            id: object.id,
            line1,
            line2,
            name,
          },
        });

        return acc;
      }, []),
    [objects],
  );

  const tles = useMemo<CatalogTle[]>(
    () => viewerObjects.map((object) => object.tle),
    [viewerObjects],
  );

  const orbitClassCounts = useMemo(
    () =>
      viewerObjects.reduce(
        (acc, object) => {
          acc[object.orbitClass] += 1;
          return acc;
        },
        {
          GEO: 0,
          GTO: 0,
          LEO: 0,
          LLEO: 0,
          MEO: 0,
          OTH: 0,
          UNKNOWN: 0,
        } satisfies Record<CatalogueOrbitClass, number>,
      ),
    [viewerObjects],
  );

  const statusClassCounts = useMemo(
    () =>
      viewerObjects.reduce(
        (acc, object) => {
          acc[object.status] += 1;
          return acc;
        },
        {
          ACTIVE: 0,
          INACTIVE: 0,
          UNKNOWN: 0,
        } satisfies Record<CatalogueStatusCategory, number>,
      ),
    [viewerObjects],
  );

  const { isFullscreen, toggleFullscreen } = useFullscreenPanel();

  return (
    <div
      aria-label="Catalogue orbital visualization"
      className={className}
      role="region"
    >
      <FullscreenPanelCard
        badge={`${tles.length} objects`}
        collapsedClassName="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        controls={
          <div className="flex min-w-0 flex-1 flex-wrap justify-between gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap gap-2">
              {STATUS_CATEGORIES.map((statusClass) => (
                <span
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600"
                  key={statusClass}
                >

                  {statusClass}
                  <span className="text-slate-400">
                    {statusClassCounts[statusClass]}
                  </span>
                </span>
              ))}
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap justify-end gap-2">
              {ORBIT_CLASSES.map((orbitClass) => (
                <span
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600"
                  key={orbitClass}
                >
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: ORBIT_CLASS_COLORS[orbitClass] }}
                  />
                  {ORBIT_CLASS_LABELS[orbitClass]}
                  <span className="text-slate-400">
                    {orbitClassCounts[orbitClass]}
                  </span>
                </span>
              ))}
            </div>
          </div>
        }
        fullscreenLabel="catalogue 3D"
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        title="3D View"
        titleClassName="min-w-0 shrink-0"
      >
        <div
          className={`h-full min-h-0 w-full bg-slate-950 ${viewerClassName}`}
        >
          {tles.length > 0 ? (
            <Visual3DWidget
              isRealTime
              showClock={false}
              showDayAndNightEffect
              showMeridiansAndParallels
              showTimeline={false}
              workersUrl={VISUAL_3D_WORKERS_URL}
            >
              <Catalogue3DTools tles={tles} />

              <TLEProviderCalculator>
                <TLEDataProvider sourceTLE={tles}>
                  {viewerObjects.map((object) => {
                    const orbitClassColor = ORBIT_CLASS_COLORS[object.orbitClass];
                    const color = hexToVisualColor(
                      orbitClassColor ?? ORBIT_CLASS_COLORS.UNKNOWN,
                    );

                    return (
                      <TLEStateProvider
                        color={color}
                        key={object.id}
                        sourceTLE={object.tle}
                      >
                        <Satellite
                          satelliteColour={color}
                          showModel={false}
                          showName={false}
                        >
                          <Orbit3D
                            leadTime={CATALOG_ORBIT_LEAD_SECONDS}
                            orbitColor={color}
                            showFutureOrbit
                            trailTime={CATALOG_ORBIT_TRAIL_SECONDS}
                          />
                        </Satellite>
                      </TLEStateProvider>
                    );
                  })}
                </TLEDataProvider>
              </TLEProviderCalculator>
            </Visual3DWidget>
          ) : (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm font-semibold text-slate-400">
              No TLE data available for catalogue visualization.
            </div>
          )}
        </div>
      </FullscreenPanelCard>
    </div>
  );
};
